import { FC, useRef } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { MediaContentTypes } from '../../types'
import { IImageContentItem } from '@obeta/data/lib/hooks/useArticleGalleryContent'
import { MediaItemsSectionProps } from './MediaItemsSection'
import { getOxomiImagesByGroup } from '../../helpers'
import { MediaItemsGrid } from './MediaItemsGrid'
import { useScrollCheck } from '../../hooks/useScrollCheck'
import styles from './MediaItemsSection.module.scss'

type MediaItemsPanelDesktopViewProps = {
  itemsByGroup: MediaItemsSectionProps['itemsByGroup']
  onSetPreview: (itemIdx: number) => void
  activeContentTypeFilter: MediaContentTypes
  activeItemIndex: number
}

const GENERAL_SECTION_TRANSLATION_MAP = {
  [MediaContentTypes.ThreeDView]: 'COMPONENTS.MEDIA_GALLERY.CONTENT_TYPE_TABS.THREE_D_VIEW',
  [MediaContentTypes.Builder]: 'COMPONENTS.MEDIA_GALLERY.CONTENT_TYPE_TABS.BUILDER',
  [MediaContentTypes.Video]: 'COMPONENTS.MEDIA_GALLERY.CONTENT_TYPE_TABS.VIDEO',
}

export const MediaItemsPanelDesktopView: FC<MediaItemsPanelDesktopViewProps> = ({
  itemsByGroup,
  onSetPreview,
  activeContentTypeFilter,
  activeItemIndex,
}) => {
  const { t } = useTranslation()
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  const hasScroll = useScrollCheck(scrollContainerRef, 'vertical')

  const getTabItems = () => {
    // Group by image type for Builder type
    if (activeContentTypeFilter === MediaContentTypes.Builder) {
      return Object.entries(
        getOxomiImagesByGroup(itemsByGroup[MediaContentTypes.Builder] as IImageContentItem[])
      )
    } else {
      // Group by activeContentTypeFilter
      return Object.entries(itemsByGroup).filter(([key, items]) => {
        if (key === MediaContentTypes.All || items.length === 0) return false
        if (activeContentTypeFilter === MediaContentTypes.All) return true

        return key === activeContentTypeFilter
      })
    }
  }

  const getGroupSectionTitle = (groupName: string) => {
    if (activeContentTypeFilter === MediaContentTypes.Builder) {
      return groupName
    }

    return activeContentTypeFilter === MediaContentTypes.All
      ? t(`${GENERAL_SECTION_TRANSLATION_MAP[groupName]}`)
      : null
  }

  return (
    <div
      className={clsx(styles.mediaItemsPanelDesktopViewScrollbar, {
        [styles.hasScroll]: hasScroll,
      })}
      ref={scrollContainerRef}
    >
      <div className={styles.mediaItemsPanelDesktopView}>
        {getTabItems().map(([groupName, items]) => (
          <MediaItemsGrid
            activeItemIndex={activeItemIndex}
            key={groupName}
            title={getGroupSectionTitle(groupName)}
            items={items}
            onSetPreview={onSetPreview}
          />
        ))}
      </div>
    </div>
  )
}
